import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  Spin,
  Alert,
  Button,
  Row,
  Col,
  message as antMessage,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import api from '../../services/api';
import {
  fetchTicketDetail,
  markTicketRead,
  postMessage,
  patchTicket,
  type TicketDetail,
  type SupportMessage,
} from '../../services/supportTicketsService';
import { TicketThread } from './components/TicketThread';
import { ReplyComposer } from './components/ReplyComposer';
import { TicketMetaPanel } from './components/TicketMetaPanel';

interface AdminOption {
  id: number;
  name?: string;
  email?: string;
}

export default function SupportTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [admins, setAdmins] = useState<AdminOption[]>([]);
  const [patchLoading, setPatchLoading] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchTicketDetail(id);
      if (res.success && res.data) {
        setDetail(res.data);
        await markTicketRead(id);
        setDetail(prev =>
          prev
            ? {
                ...prev,
                ticket: {
                  ...prev.ticket,
                  adminUnreadCount: 0,
                },
              }
            : null
        );
      } else {
        setError(res.message || 'Không tải được chi tiết');
      }
    } catch (e: unknown) {
      const ax = e as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      setError(ax.response?.data?.message || ax.message || 'Lỗi tải dữ liệu');
      antMessage.error('Không tải được ticket');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{
          success: boolean;
          data: Array<{
            id: number;
            fullName?: string;
            name?: string;
            email?: string;
          }>;
        }>('/admin/admins', { params: { limit: 100 } });
        const data = res.data?.data;
        if (Array.isArray(data) && !cancelled) {
          setAdmins(
            data.map(a => ({
              id: a.id,
              name: (a as { fullName?: string }).fullName || a.name,
              email: a.email,
            }))
          );
        }
      } catch {
        // Non–super_admin may get 403; keep empty list
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleReply = useCallback(
    async (
      text: string,
      attachmentUrls: Array<{
        url: string;
        file_name: string;
        mime_type: string;
        file_size: number;
        file_type?: string;
      }>
    ) => {
      if (!id) return;
      const optimisticMessage: SupportMessage = {
        id: `opt-${Date.now()}`,
        senderType: 'ADMIN',
        message: text,
        attachments: attachmentUrls.map(a => ({
          id: `opt-${a.url}`,
          fileType: (a.file_type as string) || 'FILE',
          fileName: a.file_name,
          url: a.url,
        })),
        createdAt: new Date().toISOString(),
      };
      setDetail(prev =>
        prev
          ? {
              ...prev,
              messages: [...prev.messages, optimisticMessage],
            }
          : null
      );
      try {
        await postMessage(id, {
          message: text,
          attachment_urls: attachmentUrls,
        });
        await loadDetail();
      } catch (e: unknown) {
        const err = e as { message?: string };
        antMessage.error(err.message || 'Gửi thất bại');
        setDetail(prev =>
          prev
            ? {
                ...prev,
                messages: prev.messages.filter(
                  m => m.id !== optimisticMessage.id
                ),
              }
            : null
        );
      }
    },
    [id, loadDetail]
  );

  const handlePatch = useCallback(
    async (patch: {
      status?: string;
      priority?: string;
      assigned_admin_id?: number | null;
    }) => {
      if (!id) return;
      setPatchLoading(true);
      const prev = detail;
      if (detail) {
        setDetail({
          ...detail,
          ticket: {
            ...detail.ticket,
            ...(patch.status && { status: patch.status }),
            ...(patch.priority && { priority: patch.priority }),
            ...(patch.assigned_admin_id !== undefined && {
              assignedAdmin:
                patch.assigned_admin_id != null
                  ? admins.find(a => a.id === patch.assigned_admin_id)
                    ? {
                        id: patch.assigned_admin_id,
                        name: admins.find(a => a.id === patch.assigned_admin_id)
                          ?.name,
                      }
                    : null
                  : null,
            }),
          },
        });
      }
      try {
        await patchTicket(id, {
          status: patch.status,
          priority: patch.priority,
          assigned_admin_id: patch.assigned_admin_id,
        });
      } catch (e: unknown) {
        const err = e as { message?: string };
        antMessage.error(err.message || 'Cập nhật thất bại');
        if (prev) setDetail(prev);
      } finally {
        setPatchLoading(false);
      }
    },
    [id, detail, admins]
  );

  if (!id) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message="Thiếu ID ticket" />
        <Button onClick={() => navigate('/support-tickets')}>
          Quay lại danh sách
        </Button>
      </div>
    );
  }

  if (loading && !detail) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message={error} showIcon />
        <Button
          onClick={() => navigate('/support-tickets')}
          style={{ marginTop: 16 }}
        >
          Quay lại danh sách
        </Button>
      </div>
    );
  }

  if (!detail) return null;

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <header className="merchant-detail-header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/support-tickets')}
          style={{ marginBottom: 8 }}
        >
          Quay lại
        </Button>
        <h1 className="merchant-detail-title">
          {detail.ticket.ticketCode} – {detail.ticket.subject}
        </h1>
        <nav className="merchant-detail-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <Link to="/support-tickets">Hỗ trợ kỹ thuật</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <span className="merchant-detail-breadcrumb-current">
            {detail.ticket.ticketCode}
          </span>
        </nav>
      </header>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card title="Hội thoại" bodyStyle={{ padding: 16 }}>
            <TicketThread messages={detail.messages} loading={false} />
            <ReplyComposer onSubmit={handleReply} />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <TicketMetaPanel
            ticket={detail.ticket}
            admins={admins}
            reportedUserContext={detail.reportedUserContext}
            onPatch={handlePatch}
            loading={patchLoading}
          />
        </Col>
      </Row>
    </div>
  );
}
